/**
 * Organization-management endpoints — proxy to Neon Auth's org plugin via rm-api
 * (`/api/v1/organizations/*`). All are session-cookie authed; the active org is
 * decided server-side from the JWT, never sent from here.
 */
import { api } from '../http'
import {
  FullOrganizationSchema,
  OrganizationSchema,
  OrganizationsResponseSchema,
  OrgInvitationSchema,
  OrgInvitationsResponseSchema,
  OrgMembersResponseSchema,
  OrgMemberSchema,
} from '../schemas'

/** Invitations addressed to the current user (onboarding accept flow). */
export const listMyInvitations = () =>
  api.get('/organizations/invitations/mine', OrgInvitationsResponseSchema)

/** Accept an invitation by id → 204, no body. The caller joins the org. */
export const acceptInvitation = (invitationId: string) =>
  api.post(`/organizations/invitations/${invitationId}/accept`)

/** Reject an invitation by id → 204, no body. The caller does not join. */
export const rejectInvitation = (invitationId: string) =>
  api.post(`/organizations/invitations/${invitationId}/reject`)

export const listOrganizations = () =>
  api.get('/organizations', OrganizationsResponseSchema)

export const createOrganization = (name: string, slug: string) =>
  api.post('/organizations', { name, slug }, OrganizationSchema)

export const getOrganization = (id: string) =>
  api.get(`/organizations/${id}`, FullOrganizationSchema)

export const getOrgMembers = (id: string) =>
  api.get(`/organizations/${id}/members`, OrgMembersResponseSchema)

export const getOrgInvitations = (id: string) =>
  api.get(`/organizations/${id}/invitations`, OrgInvitationsResponseSchema)

export const inviteMember = (id: string, email: string, role: string) =>
  api.post(`/organizations/${id}/members`, { email, role }, OrgInvitationSchema)

/** Change a member's role. Path member_id is authoritative. */
export const updateMemberRole = (id: string, memberId: string, role: string) =>
  api.post(
    `/organizations/${id}/members/${memberId}`,
    { member_id: memberId, role },
    OrgMemberSchema,
  )

/** DELETE → 204, no body. */
export const removeMember = (id: string, memberId: string) =>
  api.del(`/organizations/${id}/members/${memberId}`)

/**
 * Switch active org. The server re-issues the `access_token` cookie with the new
 * `o`-bearing JWT; the caller MUST then `refresh()` + invalidate `qk.me()` so the
 * new context propagates (see hooks/useOrganizations).
 */
export const setActiveOrganization = (id: string) =>
  api.post(`/organizations/${id}/set-active`)

/**
 * Switch back to personal context. Re-issues the `access_token` cookie with a
 * JWT that has no `o` claim. Like set-active, the caller must `refresh()` +
 * invalidate `qk.me()` afterwards. Refused (403) for org-only accounts.
 */
export const clearActiveOrganization = () =>
  api.post(`/organizations/clear-active`)
